import { F, getIntlTranslation } from '@/utils/intl';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Input, Select, Tooltip } from 'antd';
import React from 'react';
import { useIntl } from 'umi';
import { LEAD_SOURCE_TEMPLATES_ENABLED } from '../../constants';
import styles from './TemplateDrawer.less';

export interface CreateSettingsStepProps {
  /** True when the drawer was opened from a trigger row rather than marketing. */
  creatingForTrigger: boolean;
  /** Whether this event is configured per lead source at all. */
  journeySpecific: boolean;
  /** The backend's own "when is this sent" sentence for the trigger. */
  triggerDescription?: string;
  /** The default journey first, then each lead source; covered ones disabled. */
  sendTargetOptions: { value: string; label: string; disabled?: boolean }[];
  /** The drawer's memoized `EmailSettingsFields` element — see its note on why. */
  settingsFields: React.ReactNode;
  /** What the inbox mock shows: a bound field resolved, a literal as typed. */
  subject: string;
  pretext: string;
}

/**
 * Step 1 of creating: the short list of decisions, and a live mock of the
 * recipient's inbox beside them.
 *
 * Two columns because subject and pretext are the two fields whose whole point
 * is how they read in that list — showing them as a row in an inbox is the only
 * honest preview of a value that is otherwise two text inputs.
 *
 * **Rendered as a sibling of the `body` Form.Item, never as a child.** A
 * `Form.Item` with a `name` clones its child and injects `onChange`, and change
 * events from anything inside bubble straight into it — nested, typing the
 * subject wrote that text into `body`, and the template then opened in step 2 as
 * "one piece of custom HTML" with Design gone, because a non-empty unrecognised
 * body is by definition a legacy one.
 */
const CreateSettingsStep: React.FC<CreateSettingsStepProps> = ({
  creatingForTrigger,
  journeySpecific,
  triggerDescription,
  sendTargetOptions,
  settingsFields,
  subject,
  pretext,
}) => {
  const intl = useIntl();

  return (
    <div className={styles.createStepPanel}>
      {/* Two columns: the short list of decisions left, a live mock of
          the recipient's inbox right — subject and pretext are the two
          fields whose whole point is how they read in that list. */}
      <div className={styles.createSettingsGrid}>
        <div>
          {!creatingForTrigger && (
            <div className={styles.createStepIntro}>
              <F
                id="pages.emailCenter.templates.create.introMarketing"
                defaultMessage="Name it and say what recipients see in their inbox. You will design the content next."
              />
            </div>
          )}
          {/* The trigger's "when is this sent" sentence rode along with
              the hidden lead source picker — it is the only thing on
              step 1 saying what fires this email, so it stays. */}
          {creatingForTrigger &&
            !LEAD_SOURCE_TEMPLATES_ENABLED &&
            !!triggerDescription && (
              <div className={styles.createStepIntro}>{triggerDescription}</div>
            )}
          {/**
           * Creating for a specific lead source is off for now, so the
           * scope is a decision with one answer: the default journey,
           * which every lead source without its own email inherits.
           *
           * Still a registered field rather than a value slipped into
           * the store — the save reads it like any other field, the
           * preselect effect keeps filling it, and turning the choice
           * back on is the picker below taking its place.
           */}
          {creatingForTrigger && !LEAD_SOURCE_TEMPLATES_ENABLED && (
            <Form.Item name="journey_hash_id" hidden>
              <Input type="hidden" />
            </Form.Item>
          )}
          {/**
           * The "when" decision comes first: one dropdown over the
           * default journey and every lead source, since it is the one
           * create question the trigger click did not already answer.
           * Default = the default journey (inherited by every lead
           * source without its own email); a lead source gets its own
           * link instead.
           */}
          {creatingForTrigger &&
            journeySpecific &&
            LEAD_SOURCE_TEMPLATES_ENABLED && (
              <Form.Item
                label={
                  <F
                    id="pages.emailCenter.templates.create.when"
                    defaultMessage="When to send this email?"
                  />
                }
                // The backend's own "when is this sent" sentence for the
                // trigger — the context the dropdown's label asks about.
                extra={triggerDescription}
                required
              >
                {/* Addon-style prefix so the pair reads as one control;
                  the info icon says what picking a source means. */}
                <div className={styles.leadSourceRow}>
                  <span className={styles.leadSourcePrefix}>
                    <F
                      id="pages.emailCenter.templates.create.leadSourceLabel"
                      defaultMessage="Lead Source:"
                    />
                    <Tooltip
                      title={
                        <F
                          id="pages.emailCenter.templates.create.leadSourceTooltip"
                          defaultMessage="This email is sent for customer projects that were created by the selected lead source."
                        />
                      }
                    >
                      <InfoCircleOutlined className={styles.leadSourceInfo} />
                    </Tooltip>
                  </span>
                  <Form.Item
                    name="journey_hash_id"
                    noStyle
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      options={sendTargetOptions}
                      placeholder={getIntlTranslation(intl, {
                        id: 'pages.emailCenter.templates.create.leadSourcePlaceholder',
                        defaultMessage: 'Select a lead source…',
                      })}
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            )}
          {settingsFields}
        </div>
        <aside className={styles.inboxPreview}>
          <div className={styles.inboxPreviewLabel}>
            <F
              id="pages.emailCenter.templates.create.inboxPreview"
              defaultMessage="Inbox preview"
            />
          </div>
          <div className={styles.inboxCard}>
            <div className={styles.inboxRow}>
              <div className={styles.inboxAvatar}>
                {creatingForTrigger ? '📧' : '📣'}
              </div>
              <div className={styles.inboxMain}>
                <div className={styles.inboxTopLine}>
                  <span className={styles.inboxSender}>
                    <F
                      id="pages.emailCenter.templates.create.inboxSender"
                      defaultMessage="Your company"
                    />
                  </span>
                  <span className={styles.inboxTime}>09:41</span>
                </div>
                <div
                  className={`${styles.subject} ${
                    subject ? '' : styles.inboxPlaceholder
                  }`}
                >
                  {subject || (
                    <F
                      id="pages.emailCenter.templates.subjectPlaceholder"
                      defaultMessage="Enter subject"
                    />
                  )}
                </div>
                <div
                  className={`${styles.pretext} ${
                    pretext ? '' : styles.inboxPlaceholder
                  }`}
                >
                  {pretext || (
                    <F
                      id="pages.emailCenter.templates.pretextPlaceholder"
                      defaultMessage="Enter pretext"
                    />
                  )}
                </div>
              </div>
            </div>
            {/* Dimmed skeleton rows below the live one sell the frame:
                this is a list the recipient scans, not a lone card. */}
            {[0, 1].map((ghost) => (
              <div key={ghost} className={styles.inboxGhostRow}>
                <div className={styles.inboxGhostAvatar} />
                <div className={styles.inboxGhostLines}>
                  <div className={styles.inboxGhostLine} />
                  <div
                    className={`${styles.inboxGhostLine} ${styles.inboxGhostLineShort}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CreateSettingsStep;
